// src/components/course_resources/CourseResourcesAdmin.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./course_resources.css";
import { createPortal } from "react-dom";

const COURSE_OPTIONS = [
  { key: "nanoblading_hairstrokes", label: "Nano" },
  { key: "ombre_powder_brows", label: "Ombré" },
  { key: "lip_blush", label: "Lips" },
];

const typeOptions = [
  { value: "file", label: "File Upload" },
  { value: "link", label: "Link" },
  { value: "video", label: "Video" },
  { value: "pdf_url", label: "PDF URL" },
  { value: "image_url", label: "Image URL" },
];

const visibilityOptions = [
  { value: "students", label: "Students" },
  { value: "staff", label: "Staff" },
  { value: "both", label: "Both" },
];



const getOpenUrl = (r) => (r?.file?.url || r?.url || "").trim();

const cleanExt = (raw) => {
  const url = String(raw || "");
  const noQ = url.split("?")[0].split("#")[0];
  const ext = noQ.split(".").pop() || "";
  return ext.toLowerCase();
};

const getFileKind = (r) => {
  const t = String(r?.resourceType || "").toLowerCase();
  const mime = String(r?.file?.fileType || "").toLowerCase();
  const url = String(getOpenUrl(r) || "").toLowerCase();
  const ext = cleanExt(url);

  if (t === "video" || url.includes("youtube") || url.includes("vimeo")) return "video";
  if (t === "image_url") return "image";
  if (t === "pdf_url") return "pdf";

  if (mime.includes("pdf") || ext === "pdf") return "pdf";
  if (mime.startsWith("image/") || ["png", "jpg", "jpeg", "webp", "gif"].includes(ext)) return "image";
  if (mime.includes("word") || ["doc", "docx"].includes(ext)) return "docx";

  return "link";
};

const FileThumb = ({ r }) => {
  const kind = getFileKind(r);
  const href = getOpenUrl(r);

  // If image, show actual thumbnail
  if (kind === "image" && href) {
    return (
      <div className="cr-thumb cr-thumb-image" aria-hidden="true">
        <img src={href} alt="" loading="lazy" />
      </div>
    );
  }

  const label =
    kind === "pdf" ? "PDF" :
    kind === "docx" ? "DOC" :
    kind === "video" ? "VID" : "LINK";

  const cls =
    kind === "pdf" ? "cr-thumb cr-thumb-pdf" :
    kind === "docx" ? "cr-thumb cr-thumb-docx" :
    kind === "video" ? "cr-thumb cr-thumb-video" :
    "cr-thumb cr-thumb-link";

  return <div className={cls} aria-hidden="true">{label}</div>;
};

const moveItem = (arr, fromId, toId) => {
  const fromIdx = arr.findIndex((x) => x._id === fromId);
  const toIdx = arr.findIndex((x) => x._id === toId);
  if (fromIdx < 0 || toIdx < 0) return arr;
  const next = arr.slice();
  const [moved] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, moved);
  return next;
};

export default function CourseResourcesAdmin({
  resources = [],
  isLoading = false,
  error = "",

  fetchCourseResources,
  createCourseResource,
  updateCourseResource,
  deleteCourseResource,
}) {
  // accordion
  const [isOpen, setIsOpen] = useState(false);

  // list filters
  const [includeInactive, setIncludeInactive] = useState(false);
  const [filterCourseKey, setFilterCourseKey] = useState("all");

  // modal
  const [modalOpen, setModalOpen] = useState(false);

  // form state
  const [selectedCourseKeys, setSelectedCourseKeys] = useState([]);
  const [resourceType, setResourceType] = useState("file");
  const [visibility, setVisibility] = useState("students");
  const [isActive, setIsActive] = useState(true);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // drag/drop
  const dragIdRef = useRef(null);
  const [localList, setLocalList] = useState([]);

  useEffect(() => {
    fetchCourseResources && fetchCourseResources({ includeInactive });
  }, [includeInactive, fetchCourseResources]);

  const list = useMemo(() => {
    let arr = resources || [];
    if (filterCourseKey !== "all") {
      arr = arr.filter((r) => Array.isArray(r.courseKeys) && r.courseKeys.includes(filterCourseKey));
    }
    if (!includeInactive) arr = arr.filter((r) => !!r.isActive);

    // keep current persisted ordering
    return arr
      .slice()
      .sort(
        (a, b) =>
          (a.sortOrder ?? 50) - (b.sortOrder ?? 50) ||
          new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );
  }, [resources, filterCourseKey, includeInactive]);

  useEffect(() => {
    setLocalList(list);
  }, [list]);

  const toggleCourse = (k) => {
    setSelectedCourseKeys((prev) => {
      const set = new Set(prev || []);
      if (set.has(k)) set.delete(k);
      else set.add(k);
      return Array.from(set);
    });
  };

  const resetForm = () => {
    setSelectedCourseKeys([]);
    setResourceType("file");
    setVisibility("students");
    setIsActive(true);
    setTitle("");
    setDescription("");
    setUrl("");
    setFile(null);
    setEditing(null);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (r) => {
    setEditing(r);
    setSelectedCourseKeys(Array.isArray(r.courseKeys) ? r.courseKeys : []);
    setResourceType(r.resourceType || "file");
    setVisibility(r.visibility || "students");
    setIsActive(!!r.isActive);
    setTitle(r.title || "");
    setDescription(r.description || "");
    setUrl(r.url || "");
    setFile(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFile(null);
  };

  const buildFormData = (extra = {}) => {
    const fd = new FormData();
    fd.append("courseKeys", JSON.stringify(selectedCourseKeys));
    fd.append("resourceType", resourceType);
    fd.append("visibility", visibility);
    fd.append("isActive", String(isActive));
    fd.append("title", title);
    fd.append("description", description);

    // keep sortOrder stable when editing (unless reordering handles it)
    if (editing?.sortOrder != null) fd.append("sortOrder", String(editing.sortOrder));

    // allow patches (drag reorder uses this)
    Object.keys(extra || {}).forEach((k) => fd.append(k, String(extra[k])));

    if (resourceType === "file") {
      if (file) fd.append("file", file);
    } else {
      fd.append("url", url);
    }
    return fd;
  };

  const validate = () => {
    if (!selectedCourseKeys.length) return "Select at least one course (Nano/Ombré/Lips).";

    if (resourceType === "file") {
      if (!editing && !file) return "Upload a file, or switch Resource Type to URL.";
      if (editing && !file && !editing?.file?.url) return "This resource needs a file upload.";
    } else {
      if (!url) return "Paste a URL, or switch Resource Type to File Upload.";
    }

    return "";
  };

  const onSave = async () => {
    const msg = validate();
    if (msg) return alert(msg);

    setSaving(true);
    try {
      const fd = buildFormData();
      if (editing?._id) await updateCourseResource(editing._id, fd);
      else await createCourseResource(fd);

      await fetchCourseResources?.({ includeInactive });
      closeModal();
      resetForm();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    const ok = window.confirm("Delete this resource? This cannot be undone.");
    if (!ok) return;
    setSaving(true);
    try {
      await deleteCourseResource(id);
      await fetchCourseResources?.({ includeInactive });
    } finally {
      setSaving(false);
    }
  };

  const persistSortOrders = async (ordered) => {
    // normalize sortOrder = 10, 20, 30...
    for (let i = 0; i < ordered.length; i++) {
      const r = ordered[i];
      const desired = (i + 1) * 10;
      if ((r.sortOrder ?? 50) === desired) continue;

      const fd = new FormData();
      fd.append("sortOrder", String(desired));
      await updateCourseResource(r._id, fd);
    }
  };

  const onDropReorder = async (toId) => {
    const fromId = dragIdRef.current;
    if (!fromId || fromId === toId) return;

    const next = moveItem(localList, fromId, toId);
    setLocalList(next);

    setSaving(true);
    try {
      await persistSortOrders(next);
      await fetchCourseResources?.({ includeInactive });
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const previewObj = useMemo(() => {
    // modal preview object (file upload should preview as image if possible)
    if (resourceType === "file" && file) {
      const name = String(file.name || "").toLowerCase();
      const ext = (name.split(".").pop() || "").toLowerCase();
      const mime = String(file.type || "").toLowerCase();
      const isImg = mime.startsWith("image/") || ["png", "jpg", "jpeg", "webp", "gif"].includes(ext);

      const tempUrl = isImg ? URL.createObjectURL(file) : "";
      return {
        resourceType: "file",
        file: {
          url: tempUrl,
          fileType: file.type || "",
          fileName: file.name || "",
        },
        url: "",
      };
    }

    // editing existing file preview
    if (resourceType === "file" && editing?.file?.url) return editing;

    // url preview
    if (resourceType !== "file") {
      return { resourceType, url, file: { url: "", fileType: "", fileName: "" } };
    }

    return null;
  }, [resourceType, file, url, editing]);

  useEffect(() => {
    // cleanup objectURL
    if (!previewObj?.file?.url) return;
    const isObjUrl = String(previewObj.file.url).startsWith("blob:");
    if (!isObjUrl) return;

    return () => {
      try { URL.revokeObjectURL(previewObj.file.url); } catch (_) {}
    };
  }, [previewObj]);

  return (
    <div className="dash-panel">
      {/* Collapsible header */}
      <button
        type="button"
        className="dash-accordion-head"
        onClick={() => setIsOpen((v) => !v)}
      >
        <div>
          <h2 className="dash-panel-title" style={{ margin: 0 }}>
            Course Resources (Master)
          </h2>
          <p className="dash-panel-subtitle" style={{ margin: "6px 0 0" }}>
            Upload files, add links, and drag to reorder resources for students.
          </p>
        </div>

        <span className={`dash-accordion-chevron ${isOpen ? "is-open" : ""}`} aria-hidden="true">
          ▾
        </span>
      </button>

      {isOpen && (
        <div className="dash-accordion-body">
          <section className="cr-card cr-admin" style={{ marginTop: 10 }}>
            <div className="cr-head cr-head-row">
              <div />

              <div className="cr-head-actions">
                <button
                  type="button"
                  className="cr-btn"
                  onClick={openCreateModal}
                  disabled={saving || isLoading}
                >
                  + Add Resource
                </button>

                <label className="cr-toggle">
                  <input
                    type="checkbox"
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                  />
                  <span>Include inactive</span>
                </label>

                <select
                  className="cr-select"
                  value={filterCourseKey}
                  onChange={(e) => setFilterCourseKey(e.target.value)}
                >
                  <option value="all">All courses</option>
                  {COURSE_OPTIONS.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {error ? <div className="cr-error">{error}</div> : null}

            {/* List */}
            <div className="cr-admin-list" style={{ borderTop: 0, paddingTop: 0, marginTop: 6 }}>
              <div className="cr-admin-list-head">
                <div className="cr-label">Resources (drag to reorder)</div>
                {isLoading ? <div className="cr-muted">Loading…</div> : null}
              </div>

              {!localList.length ? <div className="cr-empty">No resources found.</div> : null}

              <div className="cr-list">
                {localList.map((r) => {
                  const href = getOpenUrl(r);

                  return (
                    <div
                      key={r._id}
                      className={`cr-item cr-item-admin ${r.isActive ? "" : "inactive"}`}
                      draggable
                      onDragStart={() => { dragIdRef.current = r._id; }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => onDropReorder(r._id)}
                      title="Drag to reorder"
                    >
                      {/* Preview */}
                      <FileThumb r={r} />

                      <div className="cr-item-left">
                        <div className="cr-item-title">
                          <span className="cr-title-text">{r.title || "Untitled"}</span>

                          {!r.isActive ? (
                            <span className="cr-pill danger">Hidden</span>
                          ) : (
                            <span className="cr-pill ok">Active</span>
                          )}
                        </div>

                        {r.description ? <div className="cr-item-desc">{r.description}</div> : null}

<div className="cr-meta cr-meta-clean">
  {/* Course labels (no bubbles) */}
  <div className="cr-tags">
    {(r.courseKeys || []).map((k) => {
      const label = COURSE_OPTIONS.find((c) => c.key === k)?.label || k;
      return (
        <span key={k} className="cr-tag">
          {label}
        </span>
      );
    })}
  </div>

  {/* Visibility shown as a clean “audience” line */}
  {r.visibility ? (
<div className="cr-audience">

  <span className="cr-audience-label">Audience</span>
  <span className="cr-audience-value">{r.visibility}</span>
</div>

  ) : null}
</div>


                      </div>

                      <div className="cr-admin-actions">
                        <a
                          className={`cr-btn ghost small ${!href ? "disabled" : ""}`}
                          href={href || "#"}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => !href && e.preventDefault()}
                        >
                          Open
                        </a>

                        <button
                          className="cr-btn ghost small"
                          type="button"
                          onClick={() => openEditModal(r)}
                          disabled={saving || isLoading}
                        >
                          Edit
                        </button>

                        <button
                          className="cr-btn danger small"
                          type="button"
                          onClick={() => onDelete(r._id)}
                          disabled={saving || isLoading}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Modal */}
{/* Modal (fixed to viewport via portal) */}
{modalOpen &&
  createPortal(
    <div
      className="cr-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeModal();
      }}
    >
      <div className="cr-modal" role="dialog" aria-modal="true">
        <div className="cr-modal-head">
          <div>
            <div className="cr-modal-title">{editing ? "Edit Resource" : "Add Resource"}</div>
            <div className="cr-muted">
              Upload a file or attach a link. Preview shows how students will see it.
            </div>
          </div>

          <button type="button" className="cr-modal-x" onClick={closeModal} aria-label="Close">
            ✕
          </button>
        </div>

        {/* Preview */}
        {previewObj ? (
          <div className="cr-modal-preview">
            <div className="cr-modal-preview-label">Preview</div>
            <div className="cr-row cr-row-preview" style={{ cursor: "default" }}>
              <FileThumb r={previewObj} />

              <div className="cr-row-main">
                <div className="cr-row-title">{title || "Untitled resource"}</div>
                {description ? <div className="cr-row-desc">{description}</div> : null}

                <div className="cr-row-tags">
  {(selectedCourseKeys || []).map((k) => {
    const label = COURSE_OPTIONS.find((c) => c.key === k)?.label || k;
    return (
      <span key={k} className="cr-tag">
        {label}
      </span>
    );
  })}
</div>

              </div>

              <div className={`cr-open ${getOpenUrl(previewObj) ? "" : "disabled"}`}>Open</div>
            </div>
          </div>
        ) : null}

        {/* Form */}
        <div className="cr-modal-body">
          <div className="cr-form-row">
            <div className="cr-label">Courses</div>
            <div className="cr-course-pills">
              {COURSE_OPTIONS.map((c) => {
                const on = selectedCourseKeys.includes(c.key);
                return (
                  <button
                    key={c.key}
                    type="button"
                    className={`cr-pill-btn ${on ? "on" : ""}`}
                    onClick={() => toggleCourse(c.key)}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="cr-form-grid">
            <label className="cr-field">
              <div className="cr-label">Resource Type</div>
              <select className="cr-select" value={resourceType} onChange={(e) => setResourceType(e.target.value)}>
                {typeOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="cr-field">
              <div className="cr-label">Visibility</div>
              <select className="cr-select" value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                {visibilityOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="cr-field">
              <div className="cr-label">Status</div>
              <div className="cr-toggle-btns">
                <button type="button" className={`cr-toggle-btn ${isActive ? "on" : ""}`} onClick={() => setIsActive(true)}>
                  Active
                </button>
                <button type="button" className={`cr-toggle-btn ${!isActive ? "on" : ""}`} onClick={() => setIsActive(false)}>
                  Hidden
                </button>
              </div>
            </label>
          </div>

          <div className="cr-form-grid">
            <label className="cr-field">
              <div className="cr-label">Title (optional)</div>
              <input className="cr-input" value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>

            <label className="cr-field">
              <div className="cr-label">Description (optional)</div>
              <input className="cr-input" value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>
          </div>

          {resourceType === "file" ? (
            <div className="cr-form-row">
              <div className="cr-label">Upload</div>
              <div className="cr-upload">
                <input
                  type="file"
                  className="cr-file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                <div className="cr-muted">
                  {file
                    ? `Selected: ${file.name}`
                    : editing?.file?.fileName
                    ? `Current: ${editing.file.fileName}`
                    : "No file selected"}
                </div>
              </div>
            </div>
          ) : (
            <label className="cr-field">
              <div className="cr-label">URL</div>
              <input
                className="cr-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Paste a URL (Google Drive, YouTube, PDF, etc.)"
              />
            </label>
          )}
        </div>

        <div className="cr-modal-actions">
          <button type="button" className="cr-btn" onClick={onSave} disabled={saving || isLoading}>
            {saving ? "Saving…" : editing ? "Update Resource" : "Add Resource"}
          </button>

          <button type="button" className="cr-btn ghost" onClick={closeModal} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  )}

        
        </div>
      )}
    </div>
  );
}
